import { createFileRoute } from '@tanstack/react-router';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  CopyButton,
  Divider,
  Group,
  Menu,
  Modal,
  NumberInput,
  PasswordInput,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import {
  IconAlertCircle,
  IconBuildingStore,
  IconCheck,
  IconCopy,
  IconDots,
  IconPlugConnected,
  IconRefresh,
  IconSend,
  IconSettings,
  IconUnlink,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { trpc } from '../../lib/trpc';
import { EmptyState } from '../../components/common/EmptyState';
import { QueryError } from '../../components/common/QueryError';
import { PermissionGate } from '../../components/PermissionGate';

type Provider = 'shopify' | 'woocommerce';

/** Body-variable tokens — kept in sync with ECOMMERCE_TEMPLATE_TOKENS on the API. */
type EcomToken =
  | 'customer_name'
  | 'customer_first_name'
  | 'order_number'
  | 'order_total'
  | 'currency'
  | 'item_count'
  | 'order_status'
  | 'tracking_url'
  | 'store_name'
  | 'recovery_url'
  | 'refund_amount'
  | 'shipment_status';

const TOKEN_OPTIONS: Array<{ value: EcomToken; label: string }> = [
  { value: 'customer_name', label: 'Customer name' },
  { value: 'customer_first_name', label: 'Customer first name' },
  { value: 'order_number', label: 'Order number' },
  { value: 'order_total', label: 'Order total' },
  { value: 'currency', label: 'Currency' },
  { value: 'item_count', label: 'Item count' },
  { value: 'order_status', label: 'Order status' },
  { value: 'tracking_url', label: 'Tracking URL' },
  { value: 'store_name', label: 'Store name' },
  { value: 'recovery_url', label: 'Cart recovery URL' },
  { value: 'refund_amount', label: 'Refund amount' },
  { value: 'shipment_status', label: 'Shipment status' },
];

const ORDER_TOPICS: Record<Provider, Array<{ value: string; label: string }>> = {
  shopify: [
    { value: 'orders/create', label: 'Order placed' },
    { value: 'orders/paid', label: 'Order paid' },
    { value: 'orders/fulfilled', label: 'Order fulfilled / shipped' },
    { value: 'orders/cancelled', label: 'Order cancelled' },
    { value: 'refunds/create', label: 'Refund issued' },
    { value: 'fulfillments/update', label: 'Delivered / out for delivery' },
  ],
  woocommerce: [
    { value: 'order.created', label: 'Order created' },
    { value: 'order.paid', label: 'Order paid (processing)' },
    { value: 'order.completed', label: 'Order completed / delivered' },
    { value: 'order.cancelled', label: 'Order cancelled' },
    { value: 'order.refunded', label: 'Refund issued' },
  ],
};

interface TemplateRow {
  id?: number | string | null;
  name: string;
  language: string;
  status?: string | null;
  components?: Array<{ type?: string; text?: string }>;
}

function bodyVarCount(t: TemplateRow | undefined): number {
  if (!t) return 0;
  const body = t.components?.find((c) => (c.type ?? '').toUpperCase() === 'BODY')?.text ?? '';
  const nums = new Set<number>();
  for (const m of body.matchAll(/\{\{(\d+)\}\}/g)) nums.add(Number(m[1]));
  return nums.size;
}

/**
 * Disconnecting a store silently stops every order notification its customers
 * were getting, so name that consequence before doing it.
 */
function confirmDisconnectStore(storeName: string, onConfirm: () => void) {
  modals.openConfirmModal({
    title: `Disconnect ${storeName}?`,
    children: (
      <Text size="sm">
        Order notifications will stop being sent to your customers, and abandoned-cart reminders
        won&apos;t go out. You can connect the store again later.
      </Text>
    ),
    labels: { confirm: 'Disconnect', cancel: 'Keep connected' },
    confirmProps: { color: 'red' },
    onConfirm,
  });
}

function EcommercePage() {
  const utils = trpc.useUtils();
  const list = trpc.ecommerce.list.useQuery();
  const templates = trpc.whatsapp.templates.list.useQuery();
  const [open, h] = useDisclosure(false);

  const [provider, setProvider] = useState<Provider>('shopify');
  const [shopDomain, setShopDomain] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [consumerKey, setConsumerKey] = useState('');
  const [consumerSecret, setConsumerSecret] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');

  const [configureId, setConfigureId] = useState<number | null>(null);
  const configuring = (list.data ?? []).find((c) => c.id === configureId) ?? null;
  const [testForId, setTestForId] = useState<number | null>(null);

  const resetForm = () => {
    setShopDomain('');
    setAccessToken('');
    setConsumerKey('');
    setConsumerSecret('');
    setWebhookSecret('');
  };

  const connect = trpc.ecommerce.connect.useMutation({
    onSuccess: () => {
      void utils.ecommerce.list.invalidate();
      h.close();
      resetForm();
      notifications.show({ message: 'Store connected', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const disconnect = trpc.ecommerce.disconnect.useMutation({
    onSuccess: () => void utils.ecommerce.list.invalidate(),
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const test = trpc.ecommerce.test.useMutation({
    onSuccess: (r) =>
      r.ok
        ? notifications.show({ message: 'Connection OK — credentials are valid.', color: 'green' })
        : notifications.show({
            title: 'Connection test failed',
            message: r.error,
            color: 'red',
            autoClose: 8000,
          }),
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const canConnect =
    shopDomain.trim().length >= 3 &&
    webhookSecret.trim().length >= 8 &&
    (provider === 'shopify'
      ? accessToken.trim().length >= 10
      : consumerKey.trim().length > 0 && consumerSecret.trim().length > 0);

  const submitConnect = () =>
    connect.mutate({
      provider,
      shopDomain: shopDomain.trim(),
      webhookSecret: webhookSecret.trim(),
      ...(provider === 'shopify'
        ? { accessToken: accessToken.trim() }
        : { consumerKey: consumerKey.trim(), consumerSecret: consumerSecret.trim() }),
    });

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Your store</Title>
          <Button leftSection={<IconPlugConnected size={14} />} onClick={h.open}>
            Connect a store
          </Button>
        </Group>

        <Alert color="blue" icon={<IconBuildingStore size={16} />}>
          Send order updates and cart reminders on WhatsApp. Connect your store, paste the address
          below into it, then choose which message to send for each thing that happens.
        </Alert>

        {(list.data ?? []).length > 0 && <StatsCards />}

        {list.isPending ? (
          <Skeleton height={160} />
        ) : list.isError ? (
          <QueryError
            message="Couldn't load your stores."
            detail={list.error.message}
            onRetry={() => void list.refetch()}
            retrying={list.isFetching}
          />
        ) : (list.data ?? []).length === 0 ? (
          <Card>
            <EmptyState
              icon={<IconBuildingStore size={28} />}
              title="No store connected"
              description="Connect Shopify or WooCommerce to message customers about their orders."
              action={{ label: 'Connect a store', onClick: h.open }}
            />
          </Card>
        ) : (
          <Stack gap="sm">
            {(list.data ?? []).map((c) => (
              <Card key={c.id} withBorder>
                <Stack gap="sm">
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="xs">
                      <Badge variant="light" tt="capitalize">
                        {c.provider}
                      </Badge>
                      <Text fw={600} ff="monospace" truncate maw={280}>
                        {c.shopDomain}
                      </Text>
                      <Badge color={c.status === 'active' ? 'green' : 'gray'} variant="light">
                        {c.status}
                      </Badge>
                    </Group>
                    <Group gap={4} wrap="nowrap">
                      <Button
                        variant="subtle"
                        size="compact-xs"
                        loading={test.isPending && test.variables?.connectionId === c.id}
                        onClick={() => test.mutate({ connectionId: c.id })}
                      >
                        Check connection
                      </Button>
                      <Button
                        variant="subtle"
                        size="compact-xs"
                        leftSection={<IconSettings size={13} />}
                        onClick={() => setConfigureId(c.id)}
                      >
                        Set up notifications
                      </Button>
                      <Button
                        variant="subtle"
                        size="compact-xs"
                        leftSection={<IconSend size={13} />}
                        onClick={() => setTestForId(c.id)}
                      >
                        Send a test
                      </Button>
                      {/* Disconnecting stops every order notification for this
                          store, so it lives behind a menu and a confirm rather
                          than one click on an unlabelled red icon. */}
                      <Menu position="bottom-end" withinPortal>
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray" aria-label="More actions">
                            <IconDots size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            color="red"
                            leftSection={<IconUnlink size={14} />}
                            onClick={() =>
                              confirmDisconnectStore(c.shopDomain, () =>
                                disconnect.mutate({ connectionId: c.id }),
                              )
                            }
                          >
                            Disconnect
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Group>
                  </Group>

                  <Group align="flex-end" wrap="nowrap">
                    <TextInput
                      flex={1}
                      size="xs"
                      label="Paste this into your store's notification settings"
                      readOnly
                      value={c.webhookUrl}
                      ff="monospace"
                    />
                    <CopyButton value={c.webhookUrl}>
                      {({ copy, copied }) => (
                        <Tooltip label={copied ? 'Copied' : 'Copy URL'}>
                          <ActionIcon variant="default" size="lg" onClick={copy} mb={2}>
                            {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Group>
                  <Text size="xs" c="dimmed">
                    {c.provider === 'shopify'
                      ? 'In Shopify admin → Settings → Notifications → Webhooks, add this URL for the Order created / paid / fulfilled and Checkout events.'
                      : 'In WooCommerce → Settings → Advanced → Webhooks, add this URL for the Order events. Abandoned-cart needs a helper plugin that POSTs checkout data here.'}
                  </Text>

                  {c.recentEvents.length > 0 && (
                    <>
                      <Divider />
                      <Stack gap={2}>
                        <Text size="xs" fw={600} c="dimmed">
                          Recent events
                        </Text>
                        {c.recentEvents.map((e) => (
                          <Text key={e.id} size="xs" c="dimmed">
                            {e.topic} ·{' '}
                            <Badge
                              size="xs"
                              variant="light"
                              color={
                                e.status === 'processed'
                                  ? 'green'
                                  : e.status === 'failed'
                                    ? 'red'
                                    : e.status === 'ignored'
                                      ? 'gray'
                                      : 'blue'
                              }
                            >
                              {e.status}
                            </Badge>
                            {e.error ? ` — ${e.error}` : ''}
                          </Text>
                        ))}
                      </Stack>
                    </>
                  )}
                </Stack>
              </Card>
            ))}
          </Stack>
        )}

        {(list.data ?? []).length > 0 && <AbandonedCartsCard />}
        {(list.data ?? []).length > 0 && <EventLogCard />}
      </Stack>

      {testForId !== null && (
        <TestSendModal connectionId={testForId} onClose={() => setTestForId(null)} />
      )}

      <Modal opened={open} onClose={h.close} title="Connect a store" centered>
        <Stack>
          <Select
            label="Where do you sell?"
            data={[
              { value: 'shopify', label: 'Shopify' },
              { value: 'woocommerce', label: 'WooCommerce' },
            ]}
            value={provider}
            onChange={(v) => setProvider((v as Provider) ?? 'shopify')}
          />
          <TextInput
            label={provider === 'shopify' ? 'Shop domain' : 'Store URL'}
            description={
              provider === 'shopify'
                ? 'e.g. my-store.myshopify.com'
                : 'e.g. https://store.example.com'
            }
            value={shopDomain}
            onChange={(e) => setShopDomain(e.currentTarget.value)}
          />
          {provider === 'shopify' ? (
            <PasswordInput
              label="Access token"
              description="In Shopify: Settings → Apps and sales channels → Develop apps. Create an app and allow it to read orders and checkouts."
              value={accessToken}
              onChange={(e) => setAccessToken(e.currentTarget.value)}
            />
          ) : (
            <>
              <TextInput
                label="Store key"
                value={consumerKey}
                onChange={(e) => setConsumerKey(e.currentTarget.value)}
              />
              <PasswordInput
                label="Store secret"
                value={consumerSecret}
                onChange={(e) => setConsumerSecret(e.currentTarget.value)}
              />
            </>
          )}
          <PasswordInput
            label="Signature key"
            description={
              provider === 'shopify'
                ? "Your app's API secret key. Proves order updates really came from your store."
                : 'The secret you set on each WooCommerce webhook. Proves order updates really came from your store.'
            }
            value={webhookSecret}
            onChange={(e) => setWebhookSecret(e.currentTarget.value)}
          />
          <Group justify="end">
            <Button variant="default" onClick={h.close}>
              Cancel
            </Button>
            <Button loading={connect.isPending} disabled={!canConnect} onClick={submitConnect}>
              Connect
            </Button>
          </Group>
        </Stack>
      </Modal>

      {configuring && (
        <MappingModal
          connectionId={configuring.id}
          provider={configuring.provider as Provider}
          templates={(templates.data as TemplateRow[] | undefined) ?? []}
          templatesError={templates.error?.message}
          onClose={() => setConfigureId(null)}
        />
      )}
    </motion.div>
  );
}

type TopicMap = { templateName: string | null; languageCode: string; variableMap: string[] };
type AbandonedCfg = {
  enabled: boolean;
  delayMinutes: number;
  templateName: string | null;
  languageCode: string;
  variableMap: string[];
};
type ReviewCfg = {
  enabled: boolean;
  delayDays: number;
  templateName: string | null;
  languageCode: string;
  variableMap: string[];
};

function MappingModal(props: {
  connectionId: number;
  provider: Provider;
  templates: TemplateRow[];
  /** Set when the template list failed to load, so an empty picker isn't
   *  misreported as "you have no approved templates". */
  templatesError?: string;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const mapping = trpc.ecommerce.getMapping.useQuery({ connectionId: props.connectionId });
  const save = trpc.ecommerce.setMapping.useMutation({
    onSuccess: () => {
      void utils.ecommerce.list.invalidate();
      void utils.ecommerce.getMapping.invalidate({ connectionId: props.connectionId });
      notifications.show({ message: 'Notification settings saved', color: 'green' });
      props.onClose();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const approved = useMemo(
    () => props.templates.filter((t) => !t.status || String(t.status).toUpperCase() === 'APPROVED'),
    [props.templates],
  );
  const tplOptions = approved.map((t) => ({
    value: `${t.name}|${t.language}`,
    label: `${t.name} (${t.language})`,
  }));
  const findTpl = (name: string | null, lang: string) =>
    approved.find((t) => t.name === name && t.language === lang);

  const [topics, setTopics] = useState<Record<string, TopicMap>>({});
  const [cart, setCart] = useState<AbandonedCfg>({
    enabled: false,
    delayMinutes: 60,
    templateName: null,
    languageCode: 'en',
    variableMap: [],
  });
  const [review, setReview] = useState<ReviewCfg>({
    enabled: false,
    delayDays: 3,
    templateName: null,
    languageCode: 'en',
    variableMap: [],
  });

  useEffect(() => {
    if (!mapping.data) return;
    const t: Record<string, TopicMap> = {};
    for (const [topic, m] of Object.entries(mapping.data.topics ?? {})) {
      t[topic] = {
        templateName: m.templateName ?? null,
        languageCode: m.languageCode ?? 'en',
        variableMap: m.variableMap ?? [],
      };
    }
    setTopics(t);
    const ac = mapping.data.abandonedCart;
    if (ac)
      setCart({
        enabled: ac.enabled ?? false,
        delayMinutes: ac.delayMinutes ?? 60,
        templateName: ac.templateName ?? null,
        languageCode: ac.languageCode ?? 'en',
        variableMap: ac.variableMap ?? [],
      });
    const rr = mapping.data.reviewRequest;
    if (rr)
      setReview({
        enabled: rr.enabled ?? false,
        delayDays: rr.delayDays ?? 3,
        templateName: rr.templateName ?? null,
        languageCode: rr.languageCode ?? 'en',
        variableMap: rr.variableMap ?? [],
      });
  }, [mapping.data]);

  const setTopicTemplate = (topic: string, value: string | null) => {
    if (!value) {
      setTopics((p) => ({
        ...p,
        [topic]: { templateName: null, languageCode: 'en', variableMap: [] },
      }));
      return;
    }
    const [name, lang] = value.split('|');
    const count = bodyVarCount(findTpl(name ?? null, lang ?? 'en'));
    setTopics((p) => ({
      ...p,
      [topic]: {
        templateName: name ?? null,
        languageCode: lang ?? 'en',
        variableMap: Array.from({ length: count }, (_, i) => p[topic]?.variableMap[i] ?? ''),
      },
    }));
  };

  const setTopicVar = (topic: string, idx: number, token: string | null) =>
    setTopics((p) => {
      const cur = p[topic];
      if (!cur) return p;
      const vm = [...cur.variableMap];
      vm[idx] = token ?? '';
      return { ...p, [topic]: { ...cur, variableMap: vm } };
    });

  const setCartTemplate = (value: string | null) => {
    if (!value) {
      setCart((c) => ({ ...c, templateName: null, variableMap: [] }));
      return;
    }
    const [name, lang] = value.split('|');
    const count = bodyVarCount(findTpl(name ?? null, lang ?? 'en'));
    setCart((c) => ({
      ...c,
      templateName: name ?? null,
      languageCode: lang ?? 'en',
      variableMap: Array.from({ length: count }, (_, i) => c.variableMap[i] ?? ''),
    }));
  };

  const setReviewTemplate = (value: string | null) => {
    if (!value) {
      setReview((c) => ({ ...c, templateName: null, variableMap: [] }));
      return;
    }
    const [name, lang] = value.split('|');
    const count = bodyVarCount(findTpl(name ?? null, lang ?? 'en'));
    setReview((c) => ({
      ...c,
      templateName: name ?? null,
      languageCode: lang ?? 'en',
      variableMap: Array.from({ length: count }, (_, i) => c.variableMap[i] ?? ''),
    }));
  };

  const onSave = () => {
    // Drop topics with no template chosen; keep clean variable maps. Tokens come
    // from TOKEN_OPTIONS, so the EcomToken cast matches the server's Zod enum.
    const cleanTopics: Record<
      string,
      { templateName: string; languageCode: string; variableMap: EcomToken[] }
    > = {};
    for (const [topic, m] of Object.entries(topics)) {
      if (!m.templateName) continue;
      cleanTopics[topic] = {
        templateName: m.templateName,
        languageCode: m.languageCode,
        variableMap: m.variableMap.filter((v) => v.length > 0) as EcomToken[],
      };
    }
    save.mutate({
      connectionId: props.connectionId,
      topics: cleanTopics,
      abandonedCart: {
        enabled: cart.enabled,
        delayMinutes: cart.delayMinutes,
        templateName: cart.templateName,
        languageCode: cart.languageCode,
        variableMap: cart.variableMap.filter((v) => v.length > 0) as EcomToken[],
      },
      reviewRequest: {
        enabled: review.enabled,
        delayDays: review.delayDays,
        templateName: review.templateName,
        languageCode: review.languageCode,
        variableMap: review.variableMap.filter((v) => v.length > 0) as EcomToken[],
      },
    });
  };

  return (
    <Modal opened onClose={props.onClose} title="Notification settings" size="lg" centered>
      {mapping.isPending ? (
        <Skeleton height={240} />
      ) : mapping.isError ? (
        <QueryError
          message="Couldn't load this store's notification settings."
          detail={mapping.error.message}
          onRetry={() => void mapping.refetch()}
          retrying={mapping.isFetching}
        />
      ) : (
        <Stack>
          {props.templatesError ? (
            <QueryError
              message="Couldn't load your message templates, so the pickers below are empty."
              detail={props.templatesError}
            />
          ) : (
            tplOptions.length === 0 && (
              <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
                No approved WhatsApp templates yet. Create and get a template approved under
                Outreach → Templates first, then map it here.
              </Alert>
            )
          )}

          <Text fw={600} size="sm">
            Order notifications
          </Text>
          {ORDER_TOPICS[props.provider].map((topic) => {
            const m = topics[topic.value];
            const selected = m?.templateName ? `${m.templateName}|${m.languageCode}` : null;
            return (
              <Card key={topic.value} withBorder padding="sm">
                <Stack gap="xs">
                  <Select
                    label={topic.label}
                    placeholder="Don't send anything"
                    clearable
                    data={tplOptions}
                    value={selected}
                    onChange={(v) => setTopicTemplate(topic.value, v)}
                  />
                  {m?.templateName && m.variableMap.length > 0 && (
                    <VarMapper
                      variableMap={m.variableMap}
                      onChange={(idx, token) => setTopicVar(topic.value, idx, token)}
                    />
                  )}
                </Stack>
              </Card>
            );
          })}

          <Divider label="Abandoned cart" labelPosition="left" />
          <Switch
            label="Remind people who left items in their cart"
            checked={cart.enabled}
            onChange={(e) => setCart((c) => ({ ...c, enabled: e.currentTarget.checked }))}
          />
          {cart.enabled && (
            <Card withBorder padding="sm">
              <Stack gap="xs">
                <NumberInput
                  label="Wait this long first"
                  suffix=" minutes"
                  min={5}
                  max={10080}
                  value={cart.delayMinutes}
                  onChange={(v) =>
                    setCart((c) => ({ ...c, delayMinutes: typeof v === 'number' ? v : 60 }))
                  }
                />
                <Select
                  label="Message to send"
                  placeholder="Choose a template"
                  clearable
                  data={tplOptions}
                  value={cart.templateName ? `${cart.templateName}|${cart.languageCode}` : null}
                  onChange={setCartTemplate}
                />
                {cart.templateName && cart.variableMap.length > 0 && (
                  <VarMapper
                    variableMap={cart.variableMap}
                    onChange={(idx, token) =>
                      setCart((c) => {
                        const vm = [...c.variableMap];
                        vm[idx] = token ?? '';
                        return { ...c, variableMap: vm };
                      })
                    }
                  />
                )}
                {props.provider === 'woocommerce' && (
                  <Text size="xs" c="dimmed">
                    WooCommerce has no native abandoned-checkout webhook — install a helper plugin
                    (e.g. Abandoned Cart Lite) that POSTs checkout data to the webhook URL.
                  </Text>
                )}
              </Stack>
            </Card>
          )}

          <Divider label="Review request" labelPosition="left" />
          <Switch
            label="Ask for a review once the order arrives"
            checked={review.enabled}
            onChange={(e) => setReview((c) => ({ ...c, enabled: e.currentTarget.checked }))}
          />
          {review.enabled && (
            <Card withBorder padding="sm">
              <Stack gap="xs">
                <NumberInput
                  label="Wait this long first"
                  suffix=" days"
                  min={1}
                  max={90}
                  value={review.delayDays}
                  onChange={(v) =>
                    setReview((c) => ({ ...c, delayDays: typeof v === 'number' ? v : 3 }))
                  }
                />
                <Select
                  label="Message to send"
                  placeholder="Choose a template"
                  clearable
                  data={tplOptions}
                  value={
                    review.templateName ? `${review.templateName}|${review.languageCode}` : null
                  }
                  onChange={setReviewTemplate}
                />
                {review.templateName && review.variableMap.length > 0 && (
                  <VarMapper
                    variableMap={review.variableMap}
                    onChange={(idx, token) =>
                      setReview((c) => {
                        const vm = [...c.variableMap];
                        vm[idx] = token ?? '';
                        return { ...c, variableMap: vm };
                      })
                    }
                  />
                )}
                <Text size="xs" c="dimmed">
                  Sent {review.delayDays} day(s) after an order is fulfilled / completed.
                </Text>
              </Stack>
            </Card>
          )}

          <Group justify="end">
            <Button variant="default" onClick={props.onClose}>
              Cancel
            </Button>
            <Button loading={save.isPending} onClick={onSave}>
              Save
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

function VarMapper(props: {
  variableMap: string[];
  onChange: (idx: number, token: string | null) => void;
}) {
  return (
    <Stack gap={4}>
      <Text size="xs" c="dimmed">
        Map each template variable to a value:
      </Text>
      {props.variableMap.map((token, idx) => (
        <Group key={idx} gap="xs" wrap="nowrap">
          <Badge variant="light" miw={48}>{`{{${idx + 1}}}`}</Badge>
          <Select
            flex={1}
            size="xs"
            placeholder="Pick a value"
            clearable
            data={TOKEN_OPTIONS}
            value={token || null}
            onChange={(v) => props.onChange(idx, v)}
          />
        </Group>
      ))}
    </Stack>
  );
}

function AbandonedCartsCard() {
  const carts = trpc.ecommerce.listAbandonedCarts.useQuery({ limit: 25 });
  const rows = carts.data ?? [];
  if (carts.isPending) return <Skeleton height={120} />;
  if (carts.isError)
    return (
      <QueryError
        message="Couldn't load abandoned carts."
        detail={carts.error.message}
        onRetry={() => void carts.refetch()}
        retrying={carts.isFetching}
      />
    );
  if (rows.length === 0) return null;

  const statusColor = (s: string) =>
    s === 'recovered'
      ? 'green'
      : s === 'reminded'
        ? 'blue'
        : s === 'pending' || s === 'reminding'
          ? 'yellow'
          : 'gray';

  return (
    <Card p={0} withBorder>
      <Stack gap={0}>
        <Group p="sm" pb={4}>
          <Text fw={600}>Abandoned carts</Text>
        </Group>
        <Table striped withRowBorders={false}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Customer</Table.Th>
              <Table.Th>Phone</Table.Th>
              <Table.Th>Value</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Created</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((c) => (
              <Table.Tr key={c.id}>
                <Table.Td>{c.customerName || '—'}</Table.Td>
                <Table.Td>
                  <Text size="xs" ff="monospace">
                    {c.phone || '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs">{c.total ? `${c.total} ${c.currency}`.trim() : '—'}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="xs" variant="light" color={statusColor(c.status)}>
                    {c.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {new Date(c.createdAt).toLocaleString()}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Card>
  );
}

function StatsCards() {
  const stats = trpc.ecommerce.stats.useQuery();
  if (stats.isError)
    return (
      <QueryError
        message="Couldn't load your store stats."
        detail={stats.error.message}
        onRetry={() => void stats.refetch()}
        retrying={stats.isFetching}
      />
    );
  if (stats.isPending || !stats.data) return null;
  const s = stats.data;
  const items = [
    { label: 'Orders notified', value: String(s.ordersNotified) },
    { label: 'Carts reminded', value: String(s.cartsReminded) },
    { label: 'Carts recovered', value: String(s.cartsRecovered) },
    { label: 'Recovery rate', value: `${s.recoveryRate}%` },
    { label: 'Reviews sent', value: String(s.reviewsSent) },
    { label: 'Failed', value: String(s.eventsFailed) },
  ];
  return (
    <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }} spacing="xs">
      {items.map((it) => (
        <Card key={it.label} withBorder padding="sm">
          <Text size="xl" fw={700}>
            {it.value}
          </Text>
          <Text size="xs" c="dimmed">
            {it.label}
          </Text>
        </Card>
      ))}
    </SimpleGrid>
  );
}

function EventLogCard() {
  const utils = trpc.useUtils();
  const events = trpc.ecommerce.listEvents.useQuery({ limit: 25 });
  const retry = trpc.ecommerce.retryEvent.useMutation({
    onSuccess: () => {
      void utils.ecommerce.listEvents.invalidate();
      void utils.ecommerce.list.invalidate();
      notifications.show({ message: 'Event re-processed', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const rows = events.data ?? [];
  if (events.isPending) return <Skeleton height={120} />;
  if (events.isError)
    return (
      <QueryError
        message="Couldn't load the event log."
        detail={events.error.message}
        onRetry={() => void events.refetch()}
        retrying={events.isFetching}
      />
    );
  if (rows.length === 0) return null;
  const color = (st: string) =>
    st === 'processed' ? 'green' : st === 'failed' ? 'red' : st === 'ignored' ? 'gray' : 'blue';
  return (
    <Card p={0} withBorder>
      <Group p="sm" pb={4}>
        <Text fw={600}>Event log</Text>
      </Group>
      <Table striped withRowBorders={false}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Topic</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>When</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((e) => (
            <Table.Tr key={e.id}>
              <Table.Td>
                <Text size="xs" ff="monospace">
                  {e.topic}
                </Text>
              </Table.Td>
              <Table.Td>
                <Badge size="xs" variant="light" color={color(e.status)}>
                  {e.status}
                </Badge>
                {e.error ? (
                  <Text size="xs" c="dimmed">
                    {e.error}
                  </Text>
                ) : null}
              </Table.Td>
              <Table.Td>
                <Text size="xs" c="dimmed">
                  {new Date(e.createdAt).toLocaleString()}
                </Text>
              </Table.Td>
              <Table.Td align="right">
                <Tooltip label="Re-process">
                  <ActionIcon
                    variant="subtle"
                    loading={retry.isPending && retry.variables?.id === e.id}
                    onClick={() => retry.mutate({ id: e.id })}
                  >
                    <IconRefresh size={14} />
                  </ActionIcon>
                </Tooltip>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  );
}

function TestSendModal({ connectionId, onClose }: { connectionId: number; onClose: () => void }) {
  const utils = trpc.useUtils();
  const [phone, setPhone] = useState('');
  const [kind, setKind] = useState<'order' | 'checkout'>('order');
  const send = trpc.ecommerce.sendTest.useMutation({
    onSuccess: (r) => {
      void utils.ecommerce.list.invalidate();
      void utils.ecommerce.listEvents.invalidate();
      notifications.show({
        message: `Test ${kind}: ${r.status}${r.error ? ` — ${r.error}` : ''}`,
        color: r.status === 'processed' ? 'green' : 'yellow',
        autoClose: 8000,
      });
      onClose();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  return (
    <Modal opened onClose={onClose} title="Send a test" centered>
      <Stack>
        <Select
          label="When this happens"
          data={[
            { value: 'order', label: 'Order placed (sends now)' },
            { value: 'checkout', label: 'Abandoned checkout (schedules a reminder)' },
          ]}
          value={kind}
          onChange={(v) => setKind((v as 'order' | 'checkout') ?? 'order')}
        />
        <TextInput
          label="Send to"
          description="Include the country code, e.g. +1 415 555 0100."
          placeholder="+14155550100"
          value={phone}
          onChange={(e) => setPhone(e.currentTarget.value)}
        />
        <Text size="xs" c="dimmed">
          Fires a synthetic event through the real pipeline using your mapped template. The number
          must be a valid WhatsApp number.
        </Text>
        <Group justify="end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={send.isPending}
            disabled={phone.trim().length < 8}
            onClick={() => send.mutate({ connectionId, kind, phone: phone.trim() })}
          >
            Send test
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function EcommercePageGated() {
  return (
    <PermissionGate permission="manage_integrations">
      <EcommercePage />
    </PermissionGate>
  );
}

export const Route = createFileRoute('/vendor/integrations/ecommerce')({
  component: EcommercePageGated,
});
